// 탕: 구멍 뚫린 눈밭, 탕 벽·바닥·물, 바위 테두리·히노키 나무통·통나무 탕, 떠다니는 유자·나무통(오케), 김. 프로토타입 299–425.
// 움직이지 않는 장식은 scenery에 넣고 나중에 재질별로 합친다. 물·유자·김은 world에 넣고 매 프레임 움직인다.
import * as THREE from 'three';
import { S, mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { angDiff, rnd } from '../../engine/math';
import { steamTex } from '../../engine/textures';
import { cedar, cedarDark, deckWood, oldWood, rockMats, snowMat } from './materials';
import type { Pool, QuietLayout } from './layout';

/** 탕 수면 높이. */
export const WATER_Y = -0.14;

/** 탕에 떠다니는 유자·나무통. 가까운 카피바라에 밀린다. */
export interface Floater { pool: Pool; ang: number; rad: number; dir: number; ph: number; mesh: THREE.Object3D; push: number; oke?: boolean }

export interface Baths {
  floaters: Floater[];
  /** 김 스프라이트. userData.mist면 낮게 깔리는 안개. */
  steam: THREE.Sprite[];
  /** 탕마다 물 메쉬와 원래 정점 (물결용). */
  waters: { pool: Pool; mesh: THREE.Mesh; base: Float32Array }[];
}

const waterMats = {
  milky: new THREE.MeshStandardMaterial({ color: 0x9ED7D6, roughness: 0.35, metalness: 0, transparent: true, opacity: 0.95 }),
  clear: new THREE.MeshStandardMaterial({ color: 0x5E8A84, roughness: 0.12, metalness: 0.05, transparent: true, opacity: 0.88 }),
};
const yuzuGeo = new THREE.SphereGeometry(0.11, 16, 12), yuzuMat = plush('#F2C93A', '#FFF3B0', 0.1);

/** 원형 지오메트리를 탕 테두리 모양으로 늘인다. flipY: XY 평면 지오메트리(원·링). */
function radialScale(g: THREE.BufferGeometry, P: Pool, flipY: boolean) {
  const p = g.attributes.position!;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const ax = x, az = flipY ? -y : z, rr = Math.hypot(ax, az);
    if (rr < 1e-6) continue;
    const R = P.rAt(Math.atan2(az, ax));
    p.setX(i, x * R);
    if (flipY) p.setY(i, y * R); else p.setZ(i, z * R);
  }
  g.computeVertexNormals();
}

/** 나무통. withSake면 술병과 잔을 얹는다 (물에 띄우는 것). */
export function makeOke(withSake: boolean): THREE.Group {
  const g = new THREE.Group();
  const b = mk(new THREE.CylinderGeometry(0.3, 0.27, 0.2, 20), cedar); b.position.y = 0.05; g.add(b);
  [-0.02, 0.1].forEach((h) => { const t = mk(new THREE.TorusGeometry(0.29 - h * 0.1, 0.012, 6, 24), cedarDark); t.rotation.x = Math.PI / 2; t.position.y = h; g.add(t); });
  if (withSake) {
    const tok = mk(new THREE.LatheGeometry([[0, 0], [0.07, 0.01], [0.09, 0.08], [0.07, 0.15], [0.035, 0.2], [0.04, 0.25], [0, 0.25]].map(([x, y]) => new THREE.Vector2(x, y)), 16), plush('#F2EEE6', '#FFFFFF', 0.05));
    tok.position.set(-0.07, 0.12, 0); g.add(tok);
    const cup = mk(new THREE.CylinderGeometry(0.05, 0.035, 0.05, 12), plush('#2E3A55', '#5A6C90', 0.05)); cup.position.set(0.11, 0.17, 0.05); g.add(cup);
  }
  return g;
}

export function buildBaths(world: THREE.Object3D, scenery: THREE.Object3D, L: QuietLayout): Baths {
  const out: Baths = { floaters: [], steam: [], waters: [] };

  // 눈밭: 반지름 40 원에 탕 모양 구멍
  const gShape = new THREE.Shape(); gShape.absarc(0, 6, 40, 0, Math.PI * 2, false);
  L.pools.forEach((P) => {
    const pts: THREE.Vector2[] = [], n = P.square ? 4 : 72;
    for (let i = 0; i < n; i++) {
      const a = P.square ? (Math.PI / 4 + i * Math.PI / 2) : i / n * Math.PI * 2, R = P.rAt(a);
      pts.push(new THREE.Vector2(P.x + Math.cos(a) * R, -(P.z + Math.sin(a) * R)));
    }
    gShape.holes.push(new THREE.Path(pts));
  });
  const groundGeo = new THREE.ShapeGeometry(gShape, 96); groundGeo.rotateX(-Math.PI / 2);
  const ground = new THREE.Mesh(groundGeo, plush('#E3EAEF', '#E3EAEF', 0)); ground.receiveShadow = true; world.add(ground);

  let seed = 100;
  L.pools.forEach((P) => {
    const wallMat = plush(P.kind === 'rock' ? '#4E6E70' : '#5E4A38', P.kind === 'rock' ? '#4E6E70' : '#5E4A38', 0); wallMat.side = THREE.BackSide;
    const wg = new THREE.CylinderGeometry(1, 1, 2.3, 72, 1, true); radialScale(wg, P, false);
    const wall = new THREE.Mesh(wg, wallMat); wall.position.set(P.x, -1.15, P.z); world.add(wall);
    const fg = new THREE.CircleGeometry(1, 72); radialScale(fg, P, true);
    const fl = new THREE.Mesh(fg, plush('#3F5C5E', '#3F5C5E', 0)); fl.rotation.x = -Math.PI / 2; fl.position.set(P.x, -2.3, P.z); world.add(fl);
    const rg = new THREE.RingGeometry(0.001, 1, 64, Math.max(4, Math.round(P.r * 4))); radialScale(rg, P, true);
    const water = new THREE.Mesh(rg, waterMats[P.water]); water.rotation.x = -Math.PI / 2; water.position.set(P.x, WATER_Y, P.z); world.add(water);
    out.waters.push({ pool: P, mesh: water, base: (rg.attributes.position!.array as Float32Array).slice() });

    if (P.kind === 'rock') {
      // 크기가 섞인 바위에 소복한 눈. 드나드는 틈은 비운다.
      const per = 2 * Math.PI * (P.r + 0.2), n = Math.round(per / 0.7);
      for (let i = 0; i < n; i++) {
        const a = i / n * Math.PI * 2 + rnd(seed) * 0.1;
        if (P.gaps.some((g) => Math.abs(angDiff(a, g)) < 0.75 / P.r + 0.12)) { seed++; continue; }
        const sd = seed++, back = Math.max(0, -Math.sin(a));
        const sz = (0.32 + rnd(sd) * 0.28 + back * rnd(sd + 7) * 0.45) * (0.75 + 0.25 * Math.min(1, P.r / 2.4)), sy = 0.55 + rnd(sd + 5) * 0.3;
        const R = P.rAt(a) + 0.08 + sz * 0.25;
        const rock = mk(S(+sz.toFixed(2), 12, 9), rockMats[sd % 4]!); rock.scale.set(1.25, sy, 1.0);
        const rx = P.x + Math.cos(a) * R, rz = P.z + Math.sin(a) * R;
        rock.position.set(rx, sz * 0.15, rz); rock.rotation.y = rnd(sd + 9) * Math.PI; scenery.add(rock);
        if (rnd(sd + 11) < 0.8) {
          const cap = mk(S(+(sz * 0.9).toFixed(2), 12, 7), snowMat); cap.scale.set(1.25, 0.45, 1.0); cap.castShadow = false;
          cap.position.set(rx, sz * 0.15 + sz * sy * 0.72, rz); cap.rotation.y = rock.rotation.y; scenery.add(cap);
        }
      }
    } else if (P.kind === 'hinoki') {
      // 판자로 둘러싼 삼나무 탕, 데크, 바깥쪽 울타리
      const h = P.r;
      ([[0, h + 0.15, 2 * h + 0.6, 0.3], [0, -(h + 0.15), 2 * h + 0.6, 0.3], [h + 0.15, 0, 0.3, 2 * h], [-(h + 0.15), 0, 0.3, 2 * h]] as const).forEach(([dx, dz, w, d]) => {
        const bd = mk(new THREE.BoxGeometry(w, 0.24, d), cedar); bd.position.set(P.x + dx, 0.06, P.z + dz); scenery.add(bd);
      });
      const pile = (dx: number, dz: number, sx: number, sz2: number) => { const s = mk(S(0.5, 14, 8), snowMat); s.scale.set(sx, 0.32, sz2); s.position.set(P.x + dx, 0.22, P.z + dz); scenery.add(s); };
      pile(0, -(h + 0.2), 2.2, 0.55); pile(P.x < 0 ? -(h + 0.2) : (h + 0.2), 0.2, 0.5, 1.6);
      const D = h + 1.4;
      for (let k = -D; k < D; k += 0.42) {
        ([[-D, -h - 0.3], [h + 0.3, D]] as const).forEach(([z0, z1]) => { const pl = mk(new THREE.BoxGeometry(0.38, 0.05, z1 - z0), deckWood); pl.position.set(P.x + k + 0.21, 0.025, P.z + (z0 + z1) / 2); pl.castShadow = false; scenery.add(pl); });
        if (k + 0.21 < -h - 0.3 || k + 0.21 > h + 0.3) { const pl = mk(new THREE.BoxGeometry(0.38, 0.05, 2 * h + 0.6), deckWood); pl.position.set(P.x + k + 0.21, 0.025, P.z); pl.castShadow = false; scenery.add(pl); }
      }
      [-1, 1].forEach((sd) => {
        if (Math.sign(P.x) !== sd) return;
        const fx = P.x + sd * (D + 0.1);
        for (let z = -D; z <= D; z += 0.24) { const pk = mk(new THREE.BoxGeometry(0.06, 1.7, 0.22), oldWood); pk.position.set(fx, 0.85, P.z + z); scenery.add(pk); }
        const top = mk(new THREE.BoxGeometry(0.4, 0.22, 2 * D + 0.3), snowMat); top.position.set(fx, 1.78, P.z); scenery.add(top);
        L.obstacles.push({ rect: true, x: fx, z: P.z, hw: 0.15, hd: D });
      });
      if (P.x === 0) {
        for (let x = -D; x <= D; x += 0.24) { const pk = mk(new THREE.BoxGeometry(0.22, 1.7, 0.06), oldWood); pk.position.set(P.x + x, 0.85, P.z + D + 0.1); scenery.add(pk); }
        const top = mk(new THREE.BoxGeometry(2 * D + 0.3, 0.22, 0.4), snowMat); top.position.set(P.x, 1.78, P.z + D + 0.1); scenery.add(top);
        L.obstacles.push({ rect: true, x: P.x, z: P.z + D + 0.1, hw: D, hd: 0.15 });
      }
      const ok = makeOke(false); ok.position.set(P.x + h * 0.6, 0.18, P.z - h - 0.15); scenery.add(ok);
    } else if (P.kind === 'barrel') {
      // 데크에 묻은 둥근 삼나무 통 탕
      const n = 40;
      for (let i = 0; i < n; i++) {
        const a = i / n * Math.PI * 2, st = mk(new THREE.BoxGeometry(0.22, 0.42, 0.09), cedar);
        st.position.set(P.x + Math.cos(a) * (P.r + 0.05), 0.12, P.z + Math.sin(a) * (P.r + 0.05)); st.rotation.y = -a + Math.PI / 2; scenery.add(st);
      }
      [0.02, 0.24].forEach((y) => { const t = mk(new THREE.TorusGeometry(P.r + 0.1, 0.025, 6, 48), cedarDark); t.rotation.x = Math.PI / 2; t.position.set(P.x, y, P.z); scenery.add(t); });
      const D = P.r + 1.3;
      for (let k = -D; k < D; k += 0.42) {
        const x = k + 0.21, half = Math.abs(x) < P.r + 0.15 ? Math.sqrt(Math.max(0, (P.r + 0.15) ** 2 - x * x)) : 0;
        if (half) { ([[-D, -half], [half, D]] as const).forEach(([z0, z1]) => { const pl = mk(new THREE.BoxGeometry(0.38, 0.05, z1 - z0), deckWood); pl.position.set(P.x + x, 0.025, P.z + (z0 + z1) / 2); pl.castShadow = false; scenery.add(pl); }); }
        else { const pl = mk(new THREE.BoxGeometry(0.38, 0.05, 2 * D), deckWood); pl.position.set(P.x + x, 0.025, P.z); pl.castShadow = false; scenery.add(pl); }
      }
    }

    for (let i = 0; i < P.yuzu; i++) {
      const sd = seed++;
      const m = new THREE.Mesh(yuzuGeo, yuzuMat); m.scale.set(1, 0.88, 1); world.add(m);
      out.floaters.push({ pool: P, ang: rnd(sd) * Math.PI * 2, rad: Math.sqrt(rnd(sd + 1)) * (P.r - 0.25), dir: rnd(sd + 2) < 0.5 ? -1 : 1, ph: rnd(sd + 3) * 6.28, mesh: m, push: 0.95 });
    }
    for (let i = 0; i < P.oke; i++) {
      const sd = seed++;
      const m = makeOke(true); world.add(m);
      out.floaters.push({ pool: P, ang: rnd(sd) * Math.PI * 2, rad: (0.4 + rnd(sd + 1) * 0.4) * (P.r - 0.45), dir: rnd(sd + 2) < 0.5 ? -1 : 1, ph: rnd(sd + 3) * 6.28, mesh: m, push: 1.25, oke: true });
    }
    for (let i = 0; i < P.steam; i++) {
      const sd = seed++;
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 }));
      const a = rnd(sd) * Math.PI * 2, r = Math.sqrt(rnd(sd + 1)) * P.r * 0.75;
      sp.userData = { x: P.x + Math.cos(a) * r, z: P.z + Math.sin(a) * r, off: rnd(sd + 2) * 7, life: 6 + rnd(sd + 3) * 3, k: 0.6 + 0.4 * Math.min(1, P.r / 2) };
      world.add(sp); out.steam.push(sp);
    }
    const nm = Math.max(2, Math.round(P.r * 1.6));
    for (let i = 0; i < nm; i++) {
      const sd = seed++;
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 }));
      const a = rnd(sd) * Math.PI * 2, r = Math.sqrt(rnd(sd + 1)) * P.r * 0.5;
      sp.userData = { mist: true, x: P.x + Math.cos(a) * r, z: P.z + Math.sin(a) * r, off: rnd(sd + 2) * 10, s: P.r * (1.6 + rnd(sd + 3) * 0.8) };
      world.add(sp); out.steam.push(sp);
    }
  });
  return out;
}

/** 매 프레임: 물결, 유자 떠다니기(가까운 카피바라에 밀림), 김 피어오르기. 프로토타입 tick 4008–4044. */
export function tickBaths(B: Baths, t: number, dt: number, capys: readonly { pos: THREE.Vector3; bathAmt: number }[]): void {
  for (const W of B.waters) {
    const wp = W.mesh.geometry.attributes.position!, base = W.base, P = W.pool;
    for (let i = 0; i < wp.count; i++) {
      const x = base[i * 3]!, y = base[i * 3 + 1]!;
      wp.setZ(i, 0.012 * Math.sin(x * 2.6 + t * 0.7 + P.x) + 0.01 * Math.sin(y * 3.1 - t * 0.55));
    }
    wp.needsUpdate = true; W.mesh.geometry.computeVertexNormals();
  }
  for (const y of B.floaters) {
    const P = y.pool;
    y.ang += dt * 0.04 * y.dir;
    let x = P.x + Math.cos(y.ang) * y.rad + Math.sin(t * 0.3 + y.ph) * 0.06;
    let z = P.z + Math.sin(y.ang) * y.rad + Math.cos(t * 0.25 + y.ph) * 0.06;
    for (const a of capys) {
      if (a.bathAmt < 0.3 || Math.abs(a.pos.x - x) > 1.2 || Math.abs(a.pos.z - z) > 1.2) continue;
      const dx = x - a.pos.x, dz = z - a.pos.z, d = Math.hypot(dx, dz) || 0.001, R = y.push * a.bathAmt;
      if (d < R) {
        x = a.pos.x + dx / d * R; z = a.pos.z + dz / d * R;
        const rx = x - P.x, rz = z - P.z;
        y.rad = Math.min(P.r - (y.oke ? 0.4 : 0.18), Math.hypot(rx, rz)); y.ang = Math.atan2(rz, rx);
        x = P.x + Math.cos(y.ang) * y.rad; z = P.z + Math.sin(y.ang) * y.rad;
      }
    }
    y.mesh.position.set(x, WATER_Y + (y.oke ? -0.02 : 0.035) + 0.012 * Math.sin(t * 1.2 + y.ph), z);
    y.mesh.rotation.y = y.ph + t * 0.05 * y.dir;
  }
  tickSteam(B.steam, t);
}

/** 김·안개 스프라이트 (라군도 같이 쓴다). */
export function tickSteam(list: readonly THREE.Sprite[], t: number): void {
  for (const sp of list) {
    const u = sp.userData, m = sp.material;
    if (u.mist) {
      sp.position.set(u.x + Math.sin(t * 0.08 + u.off) * 0.5, WATER_Y + 0.45 + 0.1 * Math.sin(t * 0.2 + u.off), u.z + Math.cos(t * 0.07 + u.off) * 0.5);
      const sc = u.s * (1 + 0.08 * Math.sin(t * 0.15 + u.off)); sp.scale.set(sc, sc * 0.55, 1);
      m.opacity = 0.2 + 0.06 * Math.sin(t * 0.25 + u.off);
      continue;
    }
    const q = ((t + u.off) % u.life) / u.life;
    sp.position.set(u.x + Math.sin(t * 0.3 + u.off) * 0.2 * (u.big || 1), (u.y0 != null ? u.y0 : WATER_Y + 0.1) + q * 2.8 * u.k * (u.big || 1), u.z);
    const sc = (0.9 + q * 2.4) * u.k * (u.big || 1); sp.scale.set(sc, sc, 1);
    m.opacity = Math.sin(q * Math.PI) * 0.32;
  }
}
